import {
  type UniversityMajor,
  type Highschool,
  type Middleschool,
  type University,
  type SchoolClass,
  type CertData,
  type SurveyResult,
  type UserGroupName,
} from '../../api/interfaces/sconnStudyData'
import { type CheckboxItemCertData } from '../../components/ExaminationSelect'

export type SurveyStep =
  | 'userNickname'
  | 'userGroup'
  | 'schoolName'
  | 'major'
  | 'year'
  | 'grade'
  | 'classroom'
  | 'examinations'
  | 'examinationsCollege'
export type UserGroup =
  | 'none'
  | 'college'
  | 'highschool'
  | 'repeater'
  | 'middleschool'
  | 'elemantaryschool'
  | 'general'
  | 'generalCollege'
export type UserTypeName = 'E' | 'M' | 'H' | 'H4' | 'UC' | 'AG'
export type HasClassUserType = Extract<UserTypeName, 'M' | 'H'>
export type OkNo = 'ok' | 'no'

/**
 * V2 시험 선택 step에서 선택한 항목.
 * 제출 시 source가 'keyword'면 keywordId를 examKeywordIds로,
 * 'search'/'custom'이면 label을 examCustomNames로 전달한다.
 */
export type SelectedExamItem = {
  id: string
  label: string
  source: 'keyword' | 'search' | 'custom'
  /** 선택한 키워드가 속한 대분류 카드 id (카드 뱃지 카운트용) */
  categoryId?: string
  /** source가 'keyword'일 때 학습키워드 id */
  keywordId?: number
}

export type SurveyState = {
  steps: SurveyStep[]
  activeStep: number
  isSkipFirstStep: boolean
  isSkipLastStep: boolean
  userGroup: UserGroup | null
  surveyed: {
    userType: UserTypeName | null
    university: University | null
    middleschool: Middleschool | null
    highschool: Highschool | null
    major: UniversityMajor | null
    year: string | null
    grade: SchoolClass[] | null
    classroom: SchoolClass | null
    examinations: CertData[] | null
  }
  hasUserSearch: boolean
  checkedUserGroup: UserGroup | null
  checkboxExaminations: CheckboxItemCertData[]
  checkboxIsUniversity: OkNo | null
  selectedExamItems: SelectedExamItem[]
  /** V2 그룹 선택 step에서 고른 사용자 그룹 — 제출 시 userGroup으로 전달 */
  selectedUserGroup: UserGroupName | null
  /** 초·중·고 그룹일 때 선택한 학년 — 제출 시 userGroupGrade로 전달 */
  selectedUserGroupGrade: number | null
  surveyIsPending: boolean
  surveyOnModal: boolean
  surveyResult: SurveyResult
}

export const SURVEY_SET_USER_GROUP = 'SURVEY/SET_USER_GROUP' as const
export const SURVEY_SET_STEPS = 'SURVEY/SET_STEPS' as const
export const SURVEY_NEXT_STEPS = 'SURVEY/NEXT_STEPS' as const
export const SURVEY_PREV_STEPS = 'SURVEY/PREV_STEPS' as const
export const SURVEY_SET_START_STEP = 'SURVEY/SET_START_STEP' as const
export const SURVEY_SET_SKIP_FIRST_STEP = 'SURVEY/SKIP_FIRST_STEP' as const
export const SURVEY_SET_SKIP_LAST_STEP = 'SURVEY/SKIP_LAST_STEP' as const
export const SURVEY_SET_UNIVERSITY = 'SURVEY/SET_UNIVERSITY' as const
export const SURVEY_SET_MIDDLESCHOOL = 'SURVEY/SET_MIDDLESCHOOL' as const
export const SURVEY_SET_HIGHSCHOOL = 'SURVEY/SET_HIGHSCHOOL' as const
export const SURVEY_SET_MAJOR = 'SURVEY/SET_MAJOR' as const
export const SURVEY_SET_YEAR = 'SURVEY/SET_YEAR' as const
export const SURVEY_SET_GRADE = 'SURVEY/SET_GRADE' as const
export const SURVEY_SET_CLASSROOM = 'SURVEY/SET_CLASSROOM' as const
export const SURVEY_SET_EXAMINATION = 'SURVEY/SET_EXAMINATION' as const

export const SURVEY_SET_CHECKED_USER_GROUP = 'SURVEY/SET_CHECKED_USER_GROUP' as const
export const SURVEY_SET_EXAMINATION_HAS_USER_SEARCH =
  'SURVEY/SET_EXAMINATION_HAS_USER_SEARCH' as const
export const SURVEY_SET_EXAMINATION_CHECKBOX_ITEM = 'SURVEY/SET_EXAMINATION_CHECKBOX_ITEM'
export const SURVEY_SET_CHECKBOX_IS_UNIVERSITY = 'SURVEY/SET_CHECKBOX_IS_UNIVERSITY' as const
export const SURVEY_SET_IS_PENDING = 'SURVEY/SET_SURVEY_IS_PENDING' as const
export const SURVEY_SET_ON_MODAL = 'SURVEY/SET_SURVEY_ON_MODAL' as const
export const SURVEY_SET_RESULT = 'SURVEY/SET_RESULT' as const
export const SURVEY_SET_SELECTED_EXAM_ITEMS = 'SURVEY/SET_SELECTED_EXAM_ITEMS' as const
export const SURVEY_SET_SELECTED_USER_GROUP = 'SURVEY/SET_SELECTED_USER_GROUP' as const

export type Action =
  | { type: typeof SURVEY_SET_USER_GROUP; payload: UserGroup | null }
  | {
      type: typeof SURVEY_SET_STEPS
      payload: UserGroup
    }
  | {
      type: typeof SURVEY_NEXT_STEPS
    }
  | {
      type: typeof SURVEY_PREV_STEPS
    }
  | { type: typeof SURVEY_SET_START_STEP; payload: SurveyStep }
  | {
      type: typeof SURVEY_SET_SKIP_FIRST_STEP
      payload: boolean
    }
  | {
      type: typeof SURVEY_SET_SKIP_LAST_STEP
      payload: boolean
    }
  | {
      type: typeof SURVEY_SET_UNIVERSITY
      payload: University
    }
  | {
      type: typeof SURVEY_SET_MIDDLESCHOOL
      payload: Middleschool
    }
  | {
      type: typeof SURVEY_SET_HIGHSCHOOL
      payload: Highschool
    }
  | {
      type: typeof SURVEY_SET_MAJOR
      payload: UniversityMajor
    }
  | {
      type: typeof SURVEY_SET_YEAR
      payload: string | null
    }
  | {
      type: typeof SURVEY_SET_GRADE
      payload: SchoolClass[]
    }
  | {
      type: typeof SURVEY_SET_CLASSROOM
      payload: SchoolClass | null
    }
  | {
      type: typeof SURVEY_SET_EXAMINATION
      payload: CertData[]
    }
  | {
      type: typeof SURVEY_SET_CHECKED_USER_GROUP
      payload: UserGroup | null
    }
  | {
      type: typeof SURVEY_SET_EXAMINATION_HAS_USER_SEARCH
      payload: boolean
    }
  | {
      type: typeof SURVEY_SET_EXAMINATION_CHECKBOX_ITEM
      payload: CheckboxItemCertData[]
    }
  | {
      type: typeof SURVEY_SET_CHECKBOX_IS_UNIVERSITY
      payload: OkNo
    }
  | {
      type: typeof SURVEY_SET_IS_PENDING
      payload: boolean
    }
  | {
      type: typeof SURVEY_SET_ON_MODAL
      payload: boolean
    }
  | {
      type: typeof SURVEY_SET_SELECTED_EXAM_ITEMS
      payload: SelectedExamItem[]
    }
  | {
      type: typeof SURVEY_SET_SELECTED_USER_GROUP
      payload: { userGroup: UserGroupName | null; grade: number | null }
    }
